/**
 * LeverNeedWarning — the tile/hero body for a profit-model lever (or the CRV /
 * PIP / ROIC heroes) whose inputs aren't set up yet: the unified amber "we need X
 * → fix it" warning, NEVER a sample number or a red SAMPLE badge. The founder
 * killed pretend fill-ins on the profit model; this is that same ruling for every
 * gated lever/hero, on Exec AND Financials (they used to disagree — Exec warned,
 * Financials showed a fake "$828,000/mo" behind a SAMPLE badge, founder QA 2026-07-15).
 *
 * Registry needs (costs / spend / overhead) route through the shared NeedWarning;
 * the one lever-only input with no registry entry — inventory lead time — gets the
 * matching amber block pointing at the lead-time editor on Financials.
 *
 * Extracted from ProfitModelPage so the Financials FinTile + the flagship
 * Capital-Return-Velocity card share ONE implementation (one voice, one fix route).
 */

import { Link } from "react-router-dom";
import { type NeedId } from "@data-dashboard/shared";
import { NeedWarning } from "./NeedWarning.js";

const AMBER = "#d97706";

/** Translate the profit-model service's LeverNeed signal (what a lever's math is
 *  missing) into the shared needs-registry NeedId, so a fully-unmet lever renders
 *  the SAME unified warning the gated KPI cards use. `inventory` (CRV's supplier-
 *  lead-time input) has no registry entry, so it falls through to the lead-time
 *  editor warning below (still amber, still names the gap, no fake number). */
const LEVER_NEED_TO_NEED_ID: Record<string, NeedId> = {
  contributionMargin: "product_costs",
  marketingSpend: "marketing_spend",
  overhead: "overhead",
};

export function LeverNeedWarning({ needs, compact = false }: { needs?: string; compact?: boolean }): React.ReactElement {
  const id = needs ? LEVER_NEED_TO_NEED_ID[needs] : undefined;
  if (id) return <NeedWarning need={id} compact={compact} />;
  return <InventoryNeedWarning compact={compact} />;
}

/** Inventory lead time is entered on the Financials cash-timing card — the same
 *  amber "we need X → fix it" language as NeedWarning, for the one lever input
 *  that lives outside the onboarding-needs registry. */
export function InventoryNeedWarning({ compact = false }: { compact?: boolean }): React.ReactElement {
  const link = (
    <Link
      to="/financials"
      onClick={(e) => e.stopPropagation()}
      style={{ color: AMBER, fontWeight: 700, fontSize: compact ? 10.5 : 12.5, textDecoration: "none", whiteSpace: "nowrap" }}
    >
      Add lead times →
    </Link>
  );
  if (compact) {
    return (
      <div>
        <div style={{ color: AMBER, fontSize: 14.5, fontWeight: 800, margin: "5px 0 2px" }}>
          <span aria-hidden="true">⚠️</span> Can't calculate yet
        </div>
        <div style={{ fontSize: 10.5, color: "var(--body)", lineHeight: 1.45 }}>
          We need your supplier lead times to see this. {link}
        </div>
      </div>
    );
  }
  return (
    <div
      role="status"
      style={{
        borderRadius: 10,
        border: `1px solid color-mix(in srgb, ${AMBER} 45%, var(--border))`,
        background: `color-mix(in srgb, ${AMBER} 8%, transparent)`,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: AMBER, fontWeight: 800, fontSize: 13 }}>
        <span aria-hidden="true">⚠️</span> Not set up yet
      </div>
      <div style={{ marginTop: 5, fontSize: 12.5, color: "var(--body)", lineHeight: 1.55 }}>
        We need your supplier lead times to calculate this. {link}
      </div>
    </div>
  );
}
